<template>
  <Transition name="fade">
    <div
      v-if="isExpanded"
      class="fixed inset-0 bg-gray-900 lg:hidden transition-opacity touch-none opacity-80"
      @pointerdown.stop="onTouchStart"
      @pointerup="onTouchEnd"
      @click="collapseCart"
    />
  </Transition>
  <div
    ref="cartRef"
    class="cart-wrapper touch-none lg:touch-auto"
    :class="{
      expanded: true || isExpanded,
      collapsed: false && !isExpanded,
      'transition-all': !dragActive,
      'collapsed-hidden': totalQuantity === 0,
    }"
    v-bind="$attrs"
    @pointerdown="onTouchStart"
    @pointerup="onTouchEnd"
  >
    <button
      type="button"
      class="drag-line-container"
    >
      <span class="drag-line" />
    </button>
    <div class="cart-drag-header">
      <div class="w-full flex justify-between items-center px-4">
        <div class="flex flex-col">
          <TransitionGroup
            name="fade"
            appear
          >
            <template v-if="!isExpanded">
              <span class="text-vp-base-500 text-xs font-semibold">{{ $gettext('Cart') }}</span>
              <span class="text-md text-vp-base-900 font-bold">
                <template v-if="totalQuantity > 0">{{
                  $ngettext('%{count} ticket', '%{count} tickets', totalQuantity, { count: `${totalQuantity}` })
                }}</template>
                <template v-else>{{ $gettext('Nothing here yet') }}</template>
              </span>
              <span
                v-if="props.priceBreakdown"
                class="text-vp-base-500 text-sm font-semibold"
              >{{ $gettext("Total") }}: {{ formatCurrency(props.priceBreakdown.orderTotal || 0, { currency }) }}
              </span>
            </template>
          </TransitionGroup>
        </div>
        <VPButton
          class="bg-pink-600 text-vp-base-100 text-sm font-semibold rounded-md z-20 py-1!"
          size="md"
          :label="nextLabel"
          @click="toggleExpand"
        />
      </div>
    </div>
    <Transition name="expand">
      <ShoppingCartContent
        :show-header="isExpanded"
        :hide-breakdown="!isExpanded"
        :read-only="props.readOnly"
        :show-reset="props.showReset"
        :event="props.event"
        :items="props.items"
        :price-breakdown="props.priceBreakdown"
        :control-quantity="props.controlQuantity"
        class="overscroll-contain"
        v-bind="$slots"
      >
        <template #footer>
          <slot
            name="footer"
            v-bind="{ isExpanded }"
          />
        </template>
      </ShoppingCartContent>
    </Transition>
    <div
      v-if="attribution"
      class="text-xs lg:mt-8 mx-4 lg:mx-0 my-1 lg:my-0"
    >
      <p
        class="text-right"
        v-html="poweredBy"
      />
    </div>
  </div>
</template>

<script lang="ts">

</script>

<script setup lang="ts">
import { computed, ref, onUnmounted } from 'vue';
import VPButton from './components/VPButton.vue';
import { useGettext } from 'vue3-gettext';
import { Checkout } from '@vp/common';
import ShoppingCartContent from './ShoppingCartContent.vue';
import { formatCurrency, HARD_CODED_USD_CHANGE_ME } from '@vp/common/util';
import { usePageContext } from 'vike-vue/usePageContext';
import type { PageContext } from 'vike/types';
import { isLg } from '@vp/vue-components';
import { useCartReservation } from './composables/useCartReservation';
import type { Attribution } from './utils/resolveBranding';

const pageContext = usePageContext() as PageContext & { attribution?: Attribution | null };

// The sentence is one translated string with the link inside it, so it has to go through v-html.
// The name and link come from an admin-editable white-label config, so they are escaped on the way
// in: markup in a name would render as live markup on a payment page, and a quote in the URL would
// break out of the href.
const escapeHtml = (value: unknown): string => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const { $gettext, $ngettext } = useGettext();

type Props = {
  readOnly?: boolean;
  showReset?: boolean;
  event: Checkout.CheckoutEvent.CheckoutEvent;
  items: Checkout.ShoppingCartItem.ShoppingCartItem[];
  priceBreakdown: Checkout.PriceBreakdown.PriceBreakdown;
  controlQuantity?: number;
  mobileNextLabel?: string;
};

const props = defineProps<Props>();

const currency = computed(() => {
  if (props.event.venue?.currency) {
    return props.event.venue?.currency;
  }
  return HARD_CODED_USD_CHANGE_ME;
});

const attribution = computed(() => pageContext.attribution ?? null);

const attributionMarkup = (resolved: Attribution): string => {
  const name = escapeHtml(resolved.text);

  if (!resolved.url) {
    return `<span class="text-vp-base font-bold">${name}</span>`;
  }

  return `<a class="text-vp-base font-bold" href="${escapeHtml(resolved.url)}" target="_blank" rel="noopener noreferrer">${name}</a>`;
};

const poweredBy = computed(() => {
  if (!attribution.value) {
    return '';
  }

  return $gettext('Powered by %{link}', { link: attributionMarkup(attribution.value) }, true);
});

const totalQuantity = computed(() => props.controlQuantity || props.items.length || 0);

const nextLabel = computed(() => {
  if (isExpanded.value) {
    return $gettext('Hide');
  }
  return props.mobileNextLabel ?? $gettext('Next');
});

const cartRef = ref();
const isExpanded = ref(false);
const touchStartY = ref(0);
const touchStartTime = ref(0);
const touchYDelta = ref(0);
const startHeight = ref(0);
const dragActive = ref(false);

// The screens behind the fixed mobile sheet reserve space for it, and only the sheet
// knows how tall it is. Expanded and mid-drag are not resting states -- see the
// composable for why neither may move the reservation.
useCartReservation(cartRef, () => !isExpanded.value && !dragActive.value);

const getThresholdHeight = () => window.innerHeight * 0.5;

const windowOnTouchMove = (e: PointerEvent) => {
  onTouchMove(e);
};

const enableTouchMove = () => {
  window.addEventListener('pointermove', windowOnTouchMove);
};

const disableTouchMove = () => {
  window.removeEventListener('pointermove', windowOnTouchMove);
};

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    cartRef.value?.style.removeProperty('height');
  }
};

const onTouchStart = (e: PointerEvent) => {
  if (isLg) {
    return;
  }

  const cart = cartRef.value;
  if (!cart) {
    return;
  }
  enableTouchMove();

  touchStartY.value = e.clientY;
  touchStartTime.value = Date.now().valueOf();
  touchYDelta.value = 0;

  const { height } = cart.getBoundingClientRect();

  startHeight.value = height;
  dragActive.value = true;
};

const onTouchMove = (e: PointerEvent) => {
  const cart = cartRef.value;
  e.preventDefault();
  if (!cart) {
    return;
  }
  if (!dragActive.value) {
    return;
  }

  touchYDelta.value = touchStartY.value - e.clientY;
  let newHeight = startHeight.value + touchYDelta.value;

  cart.style.height = `${newHeight}px`;
};

const onTouchEnd = () => {
  disableTouchMove();
  // Cleared here rather than in each branch below, none of which covers a tap
  // (touchYDelta === 0) -- which is what pressing the header button produces, since the
  // pointer handlers sit on the wrapper around it. A latched dragActive would freeze the
  // bottom-sheet reservation for the rest of the session.
  dragActive.value = false;
  const cart = cartRef.value;
  if (!cart) {
    return;
  }

  const timeDelta = Date.now().valueOf() - touchStartTime.value;
  touchStartTime.value = 0;

  const speed = touchYDelta.value / timeDelta;

  if (!isExpanded.value && (speed > 0.1 || touchYDelta.value > 70)) {
    isExpanded.value = true;
  } else if (isExpanded.value && (speed < -0.1 || touchYDelta.value < -70)) {
    isExpanded.value = false;
  } else if (touchYDelta.value !== 0) {
    const { height } = cart.getBoundingClientRect();

    isExpanded.value = height > getThresholdHeight();
  }

  cart.style.removeProperty('height');
};

const collapseCart = () => isExpanded.value = false;

onUnmounted(() => {
  window.removeEventListener('pointermove', windowOnTouchMove);
});
</script>
